/**
 * SubscriptionManagement — Manage active subscription.
 * Route: /settings/subscription
 *
 * Shows plan details, renewal date, and cancel/reactivate actions.
 */

import React, { useEffect, useState } from 'react';
import { ArrowLeft, Award, User, CheckCircle, AlertCircle, Loader } from 'react-feather';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { getPremiumStatus } from '../services/premiumService';
import { cancelSubscription } from '../services/subscriptionService';
import { getVoiceChatQuotaEurW } from '../firebaseConfig';
import SEO from '../components/SEO';
import type { PremiumStatus } from '../types/premium';

interface VoiceQuota {
  isPremium: boolean;
  tier: 'free' | 'premium';
  limit: number;
  used: number;
  remaining: number;
  resetDate: string | null;
}
import '../styles/modern.css';
import '../styles/premium.css';

export default function SubscriptionManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const uid = user?.uid;

  const [status, setStatus] = useState<PremiumStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [cancelResult, setCancelResult] = useState<{ activeUntil: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [voiceQuota, setVoiceQuota] = useState<VoiceQuota | null>(null);

  useEffect(() => {
    if (!uid) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const s = await getPremiumStatus(uid);
        setStatus(s);
      } catch {
        // default
      } finally {
        setLoading(false);
      }
    })();
  }, [uid]);

  useEffect(() => {
    if (!uid) return;
    getVoiceChatQuotaEurW({})
      .then((res) => setVoiceQuota(res.data as VoiceQuota))
      .catch((err) => console.error('voice quota fetch failed:', err));
  }, [uid]);

  const handleCancel = async () => {
    setCancelling(true);
    setError(null);
    try {
      const result = await cancelSubscription();
      setCancelResult(result);
      setCancelConfirm(false);
      // Refresh status
      if (uid) {
        const s = await getPremiumStatus(uid);
        setStatus(s);
      }
    } catch (err: any) {
      setError(err?.message || 'failed to cancel subscription. please try again.');
    } finally {
      setCancelling(false);
    }
  };

  const isPremium = status?.isPremium ?? false;
  const isWebSub = status?.store === 'stripe' || status?.store === 'web';
  const isAppSub = status?.store === 'APP_STORE' || status?.store === 'PLAY_STORE';

  const formatDate = (dateVal: any): string => {
    if (!dateVal) return '--';
    const d = dateVal.toDate ? dateVal.toDate() : new Date(dateVal);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const storeName = (store: string | undefined): string => {
    switch (store) {
      case 'APP_STORE': return 'Apple App Store';
      case 'PLAY_STORE': return 'Google Play Store';
      case 'stripe':
      case 'web': return 'Web';
      default: return store || 'Unknown';
    }
  };

  if (!user) {
    return (
      <div className="page-shell">
        <div className="page-shell__inner page-shell__inner--narrow">
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>please sign in to manage your subscription.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-shell">
      <div className="page-shell__inner page-shell__inner--narrow">
        <SEO title="Subscription | pplkpr" description="Manage your pplkpr subscription" path="/settings/subscription" />

        <button className="up-back" onClick={() => navigate(-1)} style={{ marginBottom: 20 }}>
          <ArrowLeft size={14} />
          <span>back</span>
        </button>

        {loading ? (
          <div className="glass-panel" style={{ padding: 40, textAlign: 'center' }}>
            <div style={{ fontSize: 14, color: 'var(--text-muted)' }}>loading...</div>
          </div>
        ) : (
          <>
            <h1 className="page-header__title" style={{ marginBottom: 24 }}>subscription</h1>

            {/* Plan status */}
            <div className="glass-panel" style={{ padding: 24, marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <div className="premium-crown" style={{ margin: 0 }}>
                  {isPremium ? <Award size={28} /> : <User size={28} />}
                </div>
                <div>
                  <span className={`premium-badge ${isPremium ? 'premium' : 'free'}`}>
                    {isPremium ? 'Premium' : 'Free'} Plan
                  </span>
                </div>
              </div>

              {(isPremium || voiceQuota) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {isPremium && status?.expiry && (
                    <div className="settings-row">
                      <span className="settings-row-label">
                        {cancelResult ? 'active until' : 'renewal date'}
                      </span>
                      <span className="settings-row-value">
                        {formatDate(cancelResult?.activeUntil || status.expiry)}
                      </span>
                    </div>
                  )}

                  {isPremium && (
                    <div className="settings-row">
                      <span className="settings-row-label">billed via</span>
                      <span className="settings-row-value">{storeName(status?.store)}</span>
                    </div>
                  )}

                  {voiceQuota && (
                    <div className="settings-row">
                      <span className="settings-row-label">voice chat</span>
                      <span className="settings-row-value">
                        {voiceQuota.tier === 'premium'
                          ? `${voiceQuota.used.toFixed(1)} / ${voiceQuota.limit} min${voiceQuota.resetDate ? ` · resets ${new Date(voiceQuota.resetDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}` : ''}`
                          : `${voiceQuota.used} / ${voiceQuota.limit} free sessions`}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Benefits */}
            {isPremium && (
              <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
                <div className="ep-section-label">your benefits</div>
                {[
                  'completely ad-free experience',
                  'enhanced user interface',
                  'voice chat with fate',
                  voiceQuota?.tier === 'premium'
                    ? `${voiceQuota.limit} minutes of voice chat monthly`
                    : 'monthly voice chat minutes',
                  'early access to new features',
                ].map((b) => (
                  <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', fontSize: 14, color: 'var(--text-secondary)' }}>
                    <CheckCircle size={14} style={{ color: '#4CAF50', flexShrink: 0 }} />
                    {b}
                  </div>
                ))}
              </div>
            )}

            {/* Cancel result banner */}
            {cancelResult && (
              <div className="glass-panel" style={{ padding: 16, marginBottom: 16, borderColor: 'rgba(255,193,7,0.2)' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <AlertCircle size={16} style={{ color: '#ffc107', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                    your subscription has been cancelled. you'll keep premium access until{' '}
                    <strong style={{ color: 'var(--text-primary)' }}>
                      {new Date(cancelResult.activeUntil).toLocaleDateString()}
                    </strong>.
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="glass-panel" style={{ padding: 12, marginBottom: 16, borderColor: 'rgba(255,107,107,0.3)', color: '#ff6b6b', fontSize: 13 }}>
                {error}
              </div>
            )}

            {/* Actions */}
            {isPremium && isWebSub && !cancelResult && (
              <div className="glass-panel" style={{ padding: 20, marginBottom: 16 }}>
                {!cancelConfirm ? (
                  <div style={{ textAlign: 'center' }}>
                    <button
                      className="ep-ghost-btn"
                      style={{ color: '#ff6b6b', fontSize: 13 }}
                      onClick={() => setCancelConfirm(true)}
                    >
                      cancel subscription
                    </button>
                  </div>
                ) : (
                  <div>
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>
                      are you sure? you'll keep premium until the end of your current billing period.
                    </p>
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                      <button
                        className="ep-ghost-btn"
                        style={{ fontSize: 13 }}
                        onClick={() => setCancelConfirm(false)}
                        disabled={cancelling}
                      >
                        keep subscription
                      </button>
                      <button
                        className="primary-button"
                        style={{ background: '#ff6b6b', padding: '8px 16px', fontSize: 13 }}
                        onClick={handleCancel}
                        disabled={cancelling}
                      >
                        {cancelling ? <><Loader size={12} className="spin" /> cancelling...</> : 'yes, cancel'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {isPremium && isAppSub && (
              <div className="glass-panel" style={{ padding: 20, marginBottom: 16, textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                  manage your subscription through the{' '}
                  {status?.store === 'APP_STORE' ? 'App Store' : 'Google Play Store'}{' '}
                  where you originally subscribed.
                </p>
              </div>
            )}

            {!isPremium && (
              <div className="glass-panel" style={{ padding: 24, marginBottom: 16, textAlign: 'center' }}>
                <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 16 }}>
                  you're on the free plan.
                </p>
                <Link
                  to="/premium"
                  className="primary-button"
                  style={{ textDecoration: 'none', padding: '12px 28px', fontSize: 14 }}
                >
                  upgrade to premium
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
